import { useState, useRef } from 'react';

const dnsList = [
    {
        name: '阿里云DNS',
        ipv4: ['223.5.5.5', '223.6.6.6'],
        ipv6: ['2400:3200::1', '2400:3200:baba::1'],
        doh: 'dns.alidns.com',
        dot: 'dns.alidns.com',
        official: false
    },
    {
        name: '腾讯云DNS',
        ipv4: ['119.29.29.29', '182.254.116.116'],
        ipv6: '2402:4e00::',
        doh: ['doh.pub', 'sm2.doh.pub（国密）'],
        dot: 'dot.pub',
        official: false
    },
    {
        name: '百度DNS',
        ipv4: '180.76.76.76',
        ipv6: '2400:da00::6666',
        doh: '',
        dot: '',
        official: false
    },
    {
        name: '114DNS',
        ipv4: ['114.114.114.114', '114.114.115.115'],
        ipv6: '',
        doh: '',
        dot: '',
        official: false
    },
    {
        name: 'Cloudflare',
        ipv4: ['1.1.1.1', '1.0.0.1'],
        ipv6: ['2606:4700:4700::1111', '2606:4700:4700::1001'],
        doh: 'one.one.one.one',
        dot: 'one.one.one.one',
        official: false
    },
    {
        name: 'Google',
        ipv4: ['8.8.8.8', '8.8.4.4'],
        ipv6: ['2001:4860:4860::8888', '2001:4860:4860::8844'],
        doh: 'dns.google',
        dot: 'dns.google',
        official: false
    },
    {
        name: 'Quad9',
        ipv4: ['9.9.9.9', '149.112.112.112'],
        ipv6: ['2620:fe::fe', '2620:fe::9'],
        doh: 'dns.quad9.net',
        dot: 'dns.quad9.net',
        official: false
    },
    { name: '官方DNS', ipv4: '', ipv6: '', doh: '', dot: '', official: true }
];

// 公用字段渲染方法
const renderField = (value: string | string[], official = false, color = 'text-indigo-700') => {
    if (official) {
        return <span className="text-gray-400 italic font-medium">敬请期待</span>;
    }
    if (!value) {
        return <span className="text-gray-400">-</span>;
    }
    if (Array.isArray(value)) {
        return (
            <div className="space-y-1">
                {value.map((v, i) => (
                    <span key={i} className={`${color} block font-medium`}>{v}</span>
                ))}
            </div>
        );
    }
    return <span className={`${color} font-medium`}>{value}</span>;
};

// DoH / DoT 渲染方法
const renderSupport = (value: string | string[], official = false) => {
    if (official) {
        return <span className="text-gray-400 italic">-</span>;
    }
    if (!value) {
        return <span className="text-gray-400">不支持</span>;
    }
    if (Array.isArray(value)) {
        return (
            <div className="space-y-1">
                {value.map((v, i) => (
                    <span key={i} className="text-emerald-600 block font-medium">{v}</span>
                ))}
            </div>
        );
    }
    return <span className="text-emerald-600 font-medium">{value}</span>;
};

const Tooltip = ({ text }: { text: string }) => {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const iconRef = useRef<SVGSVGElement | null>(null);

    const handleMouseEnter = () => {
        if (iconRef.current) {
            const rect = iconRef.current.getBoundingClientRect();
            setPosition({
                x: rect.left + rect.width / 2,
                y: rect.top,
            });
            setVisible(true);
        }
    };

    const handleMouseLeave = () => setVisible(false);

    return (
        <>
            <svg
                ref={iconRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 inline text-white opacity-90 ml-1 cursor-help hover:opacity-100 transition-opacity duration-200"
                fill="currentColor"
                viewBox="0 0 20 20"
            >
                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.75-9.75h1.5v4.5h-1.5v-4.5zm0-2.25h1.5v1.5h-1.5v-1.5z" />
            </svg>

            {visible && (
                <div
                    className="fixed z-50 transform -translate-x-1/2 -translate-y-full flex flex-col items-center animate-tooltip"
                    style={{ left: position.x, top: position.y - 10 }}
                >
                    <div className="relative px-4 py-2.5 text-xs font-medium text-indigo-900 bg-gradient-to-b from-white to-indigo-50 rounded-lg shadow-lg shadow-indigo-300/30 border border-indigo-100 whitespace-nowrap">
                        {text}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-indigo-50 rotate-45 border-r border-b border-indigo-100"></div>
                    </div>
                </div>
            )}
        </>
    );
};

const PublicDNSList = () => {
    return (
        <section className="py-24 px-6 bg-white text-center">
            <h2 className="text-4xl font-bold text-gray-800 mb-16">推荐公共DNS</h2>
            <div className="max-w-7xl mx-auto bg-white/80 border border-gray-200 shadow-xl rounded-3xl p-10">
                <div className="overflow-x-auto">
                    <table className="min-w-full w-full table-fixed text-sm border-separate border-spacing-0">
                        <thead>
                            <tr className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                                <th className="w-1/5 px-6 py-4 text-center font-bold tracking-wider rounded-tl-2xl border-b border-purple-400">
                                    提供商
                                </th>
                                <th className="w-1/5 px-6 py-4 text-center font-bold tracking-wider border-b border-purple-400">
                                    IPv4 <Tooltip text="IPv4 DNS服务器地址" />
                                </th>
                                <th className="w-1/5 px-6 py-4 text-center font-bold tracking-wider border-b border-purple-400">
                                    IPv6 <Tooltip text="IPv6 DNS服务器地址" />
                                </th>
                                <th className="w-1/5 px-6 py-4 text-center font-bold tracking-wider border-b border-purple-400">
                                    DoH <Tooltip text="DNS over HTTPS，通过HTTPS协议进行DNS解析" />
                                </th>
                                <th className="w-1/5 px-6 py-4 text-center font-bold tracking-wider rounded-tr-2xl border-b border-purple-400">
                                    DoT <Tooltip text="DNS over TLS，通过TLS协议进行DNS解析" />
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white">
                            {dnsList.map((dns, index) => (
                                <tr
                                    key={index}
                                    className="hover:bg-indigo-50/40 transition-colors duration-200 border-b border-gray-100 last:border-0"
                                >
                                    <td className="px-6 py-4 text-center">
                                        <div className="font-semibold text-indigo-900">{dns.name}</div>
                                    </td>
                                    <td className="px-6 py-4 text-center">{renderField(dns.ipv4, dns.official)}</td>
                                    <td className="px-6 py-4 text-center">{renderField(dns.ipv6, dns.official)}</td>
                                    <td className="px-6 py-4 text-center">{renderSupport(dns.doh, dns.official)}</td>
                                    <td className="px-6 py-4 text-center">{renderSupport(dns.dot, dns.official)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
};

export default PublicDNSList;